<template>
  <div class="resizable-aside-wrapper">
    <el-aside
      :class="['resizable-aside', { 'aside-collapsed': asideCollapsed, 'is-dragging': asideDragging }]"
      :style="{ width: asideWidth + 'px' }"
    >
      <div v-show="asideWidth > 0" class="resizable-aside-content">
        <slot></slot>
      </div>
    </el-aside>
    <div
      class="resize-bar"
      :class="{ 'is-collapsed': asideCollapsed, 'is-dragging': asideDragging, 'is-expanded': !asideCollapsed && asideWidth > 0 }"
      @mousedown="handleMouseDown"
      @dblclick="toggleAside"
    >
      <button
        v-if="!asideCollapsed && asideWidth > 0"
        type="button"
        class="resize-bar-toggle resize-bar-toggle--collapse"
        title="折叠侧边栏"
        @mousedown.stop
        @click.stop="handleCollapseClick"
      >
        <i class="el-icon-d-arrow-left"></i>
      </button>
      <button
        v-if="asideCollapsed"
        type="button"
        class="resize-bar-toggle resize-bar-toggle--expand"
        :title="collapseIconTitle"
        @mousedown.stop
        @click.stop="handleExpandClick"
      >
        <i class="el-icon-d-arrow-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
const DRAG_THRESHOLD = 3;

export default {
  name: 'ResizableAsidePanel',
  props: {
    defaultWidth: {
      type: Number,
      default: 250
    },
    maxWidth: {
      type: Number,
      default: 500
    },
    collapseSnap: {
      type: Number,
      default: 48
    },
    collapseIconTitle: {
      type: String,
      default: '展开侧边栏'
    },
    autoCollapse: {
      type: Boolean,
      default: true
    },
    autoCollapseDelay: {
      type: Number,
      default: 1000
    }
  },
  data() {
    return {
      asideWidth: this.defaultWidth,
      asideCollapsed: false,
      asideDragging: false,
      lastAsideWidth: this.defaultWidth,
      userManuallyExpanded: false,
      autoCollapseTimer: null
    };
  },
  mounted() {
    this.scheduleAutoCollapse();
  },
  beforeDestroy() {
    this.clearAutoCollapseTimer();
  },
  methods: {
    clearAutoCollapseTimer() {
      if (this.autoCollapseTimer) {
        clearTimeout(this.autoCollapseTimer);
        this.autoCollapseTimer = null;
      }
    },
    scheduleAutoCollapse() {
      if (!this.autoCollapse) {
        return;
      }
      this.clearAutoCollapseTimer();
      this.autoCollapseTimer = setTimeout(() => {
        this.autoCollapseTimer = null;
        if (!this.userManuallyExpanded && !this.asideCollapsed) {
          this.collapseAside();
          this.emitLayoutChange();
        }
      }, this.autoCollapseDelay);
    },
    emitLayoutChange() {
      this.$emit('layout-change');
      this.$nextTick(() => {
        window.dispatchEvent(new Event('resize'));
      });
    },
    expandAside() {
      this.asideWidth = this.lastAsideWidth || this.defaultWidth;
      this.asideCollapsed = false;
    },
    collapseAside() {
      if (this.asideWidth >= this.collapseSnap) {
        this.lastAsideWidth = this.asideWidth;
      }
      this.asideWidth = 0;
      this.asideCollapsed = true;
    },
    applyAsideSnap() {
      if (this.asideWidth < this.collapseSnap) {
        this.collapseAside();
      } else {
        this.asideCollapsed = false;
        this.lastAsideWidth = this.asideWidth;
      }
    },
    handleExpandClick() {
      this.userManuallyExpanded = true;
      this.clearAutoCollapseTimer();
      this.expandAside();
      this.emitLayoutChange();
    },
    handleCollapseClick() {
      this.collapseAside();
      this.emitLayoutChange();
    },
    toggleAside() {
      if (this.asideCollapsed || this.asideWidth === 0) {
        this.handleExpandClick();
      } else {
        this.handleCollapseClick();
      }
    },
    handleMouseDown(e) {
      if (e.button !== 0 || e.target.closest('.resize-bar-toggle')) {
        return;
      }

      let dragged = false;
      const startX = e.clientX;
      const startWidth = this.asideCollapsed ? 0 : this.asideWidth;

      if (startWidth >= this.collapseSnap) {
        this.lastAsideWidth = startWidth;
      }

      this.asideDragging = true;
      this.asideCollapsed = false;

      const handleMouseMove = (moveEvent) => {
        const deltaX = moveEvent.clientX - startX;
        if (!dragged && Math.abs(deltaX) >= DRAG_THRESHOLD) {
          dragged = true;
        }
        if (!dragged) {
          return;
        }

        const newWidth = Math.min(
          this.maxWidth,
          Math.max(0, startWidth + deltaX)
        );
        this.asideWidth = newWidth;
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        this.asideDragging = false;

        if (!dragged && this.asideWidth === 0) {
          this.expandAside();
          this.emitLayoutChange();
        } else if (dragged) {
          this.applyAsideSnap();
          this.emitLayoutChange();
        }
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
  }
};
</script>
